import { useContext, useEffect, useState } from 'react';

import { Form } from '@zillow/constellation-10';

import { propertyUtils } from '@zg-rentals/rental-manager-env';

import { ModularListingContext } from '../../../containers/CreateListingPage/context';
import { WizardContext } from '../../../components/Wizard/context';

import { useSelector } from '../../../selectors/useSelector';

import { DISPLAY_SERVER_ERROR_AT } from '../../../constants/WizardConstants';

import PageWizard from '../../../components/Wizard/PageWizard';
import WizardAlert from '../../../components/WizardAlert';
import WizardTipCardContainer from '../../../components/WizardTipCardContainer';

import WizardTipContent from './components/WizardTipContent';

import FormSectionContainer from './components/FormSectionContainer';

import { WizardFlexContainer } from '../../../styles/wizard';

import type { ReactNode } from 'react';
import type { PageWizardProps } from '../../../components/Wizard/PageWizard';

type Props = {
  onGoToPrevStep: () => void;
  onGoToNextStep: () => void;
  onExitWizard: () => void;
  wizardTitle: ReactNode;
  progressStepper: ReactNode;
  totalSteps: number;
  currentStepIndex: number;
  showDesktopViewBreakpoint: PageWizardProps['showDesktopViewBreakpoint'];
};

const DescribeProperty = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}: Props) => {
  const {
    // @ts-expect-error
    wizardState: { isFetchingData },
  } = useContext(WizardContext);

  const {
    modularListingState: { serverErrorObj },
  } = useContext(ModularListingContext);

  const currentProperty = useSelector((state) => state.properties.currentProperty);

  const [clickedNext, setClickedNext] = useState(false);
  const [clickedBackNext, setClickedBack] = useState(false);

  const isRoomForRent = propertyUtils.isRoomForRent({ currentProperty });

  const tipCardHeader = isRoomForRent ? 'Tips for descriptions' : 'Tips for property descriptions';

  useEffect(() => {
    const { fieldName } = serverErrorObj;

    if (!fieldName && !isFetchingData) {
      checkNavToStep();
    }
  }, [isFetchingData]);

  const checkNavToStep = () => {
    if (clickedNext) {
      onNextClick();
    }

    if (clickedBackNext) {
      onBackClick();
    }
  };

  const onNextClick = () => {
    if (isFetchingData) {
      setClickedNext(true);
      return;
    }

    onGoToNextStep();
  };

  const onBackClick = () => {
    if (isFetchingData) {
      setClickedBack(true);
      return;
    }

    onGoToPrevStep();
  };

  return (
    <PageWizard
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onNextClick}
      onBackClick={onBackClick}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
      {...rest}
    >
      <div data-testid="DescribeProperty">
        <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.PAGE} />

        <WizardFlexContainer>
          {/* cast as div to prevent cannot appear as a descendant of form error */}
          <Form asChild={true} onSubmit={(e: React.FormEvent<HTMLFormElement>) => e.preventDefault()}>
            <div>
              <FormSectionContainer />
            </div>
          </Form>

          <WizardTipCardContainer header={tipCardHeader} showDesktopViewBreakpoint={showDesktopViewBreakpoint}>
            <WizardTipContent />
          </WizardTipCardContainer>
        </WizardFlexContainer>
      </div>
    </PageWizard>
  );
};

export default DescribeProperty;
